import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./IntroSubtitle.scss";

// Timestamps in seconds for each paragraph of the "intro_subtitle" translation key.
// Each entry controls when that paragraph becomes visible after the intro starts.
const INTRO_PARAGRAPH_TIMESTAMPS: number[] = [
  0, // "Hello friends! Welcome to Bear Shares..."
  7, // "My name is Mary the Bear..."
  15, // "Spirit Bear is a Bearrister..."
  32, // "We made Bear Shares so you can get to know Spirit Bear..."
  40, // "Start by visiting these four PAW PORTALS..."
  49, // "After hearing his answers, complete the quiz..."
  56, // "To start each quiz, just click on the Q icon."
  61, // "The fifth PAW PORTAL in Bear Shares is my classroom..."
  74, // "Have fun!"
];

// Placeholder timestamps (in seconds) for each paragraph of the "classroom_subtitle" key.
// Tune these to match the actual timing of Intro_1A_Classroom.mp3.
const CLASSROOM_SUBTITLE_TIMESTAMPS: number[] = [
  0, // Welcome to my classroom!
  3, // Here you can click
  16, // After you've explored
  21, // If you haven't visited
  28, // If you'd like to see
  33, // Before you leave,
  44, // Let's join hands
];

/** Maps each translation key to its paragraph timestamps (in seconds). */
const SUBTITLE_TIMESTAMPS: Record<string, number[]> = {
  intro_subtitle: INTRO_PARAGRAPH_TIMESTAMPS,
  classroom_subtitle: CLASSROOM_SUBTITLE_TIMESTAMPS,
};

const getIntroSubtitleParagraphs = (text: string): string[] =>
  text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

interface IntroSubtitleProps {
  isIntroPlaying: boolean;
  /** i18n key to use for subtitle text. Defaults to "intro_subtitle". */
  translationKey?: string;
  /** Timestamps in seconds for each paragraph. Defaults to the built-in map for the given translationKey. */
  timestamps?: number[];
}

export default function IntroSubtitle({
  isIntroPlaying,
  translationKey = "intro_subtitle",
  timestamps,
}: IntroSubtitleProps) {
  const { t } = useTranslation();
  const [visibleParagraphCount, setVisibleParagraphCount] = useState(0);

  const resolvedTimestamps =
    timestamps ?? SUBTITLE_TIMESTAMPS[translationKey] ?? [];

  useEffect(() => {
    if (!isIntroPlaying) {
      setVisibleParagraphCount(0);
      return;
    }

    const paragraphs = getIntroSubtitleParagraphs(t(translationKey));
    const timerIds: NodeJS.Timeout[] = [];

    paragraphs.forEach((_, index) => {
      const startAt = resolvedTimestamps[index];
      if (typeof startAt !== "number") return;

      const timerId = setTimeout(() => {
        setVisibleParagraphCount((prev) => Math.max(prev, index + 1));
      }, startAt * 1000);

      timerIds.push(timerId);
    });

    return () => {
      timerIds.forEach(clearTimeout);
      setVisibleParagraphCount(0);
    };
  }, [isIntroPlaying, t, translationKey, resolvedTimestamps]);

  const paragraphs = getIntroSubtitleParagraphs(t(translationKey));
  const currentParagraph =
    visibleParagraphCount > 0
      ? (paragraphs[visibleParagraphCount - 1] ?? "")
      : "";

  if (!isIntroPlaying || !currentParagraph) return null;

  return (
    <div className="IntroSubtitle">
      <p
        dangerouslySetInnerHTML={{
          __html: currentParagraph,
        }}
        key={visibleParagraphCount}
        className="IntroParagraphTransition"
      ></p>
    </div>
  );
}
